import {
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
} from '@fluentui/react-components';
import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';

import type { ConversationDocument } from '../../utils/parseConversation.js';
import type { TranscriptTurn } from '../../utils/parseTranscript.js';
import { TranscriptViewer } from '../TranscriptViewer/TranscriptViewer.js';

import { useStyles } from './ConversationViewer.style.js';

export interface ConversationViewerProps {
  conversation: ConversationDocument;
  isScrollable?: boolean;
  /** When true, the transcript fills its parent height and scrolls internally. */
  fillHeight?: boolean;
  /** Optional heading rendered inline with the message count. */
  heading?: ReactNode;
  /** Optional action rendered right-aligned on the heading/summary row. */
  headerAction?: ReactNode;
  /** Optional hover-revealed action rendered inside each transcript turn. */
  renderTurnAction?: (turn: TranscriptTurn, index: number) => ReactNode;
  /** Optional content rendered directly below each transcript turn. */
  renderTurnAttachment?: (turn: TranscriptTurn, index: number) => ReactNode;
}

export function ConversationViewer({
  conversation,
  isScrollable = false,
  fillHeight = false,
  heading,
  headerAction,
  renderTurnAction,
  renderTurnAttachment,
}: ConversationViewerProps) {
  const styles = useStyles();
  const [selectedSessionId, setSelectedSessionId] = useState(
    conversation.sessions[0]?.id ?? '',
  );
  const selectedSession =
    conversation.sessions.find(({ id }) => id === selectedSessionId) ??
    conversation.sessions[0];

  if (selectedSession === undefined) {
    return (
      <MessageBar className={styles.empty} intent="info" role="status">
        <MessageBarBody>This conversation has no sessions.</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <Fragment>
      {conversation.sessions.length > 1 && (
        <Field className={styles.sessionField} label="Session">
          <Dropdown
            aria-label="Session"
            value={selectedSession.label}
            selectedOptions={[selectedSession.id]}
            onOptionSelect={(_event, data) => {
              if (data.optionValue !== undefined) {
                setSelectedSessionId(data.optionValue);
              }
            }}
          >
            {conversation.sessions.map((session) => (
              <Option key={session.id} value={session.id}>
                {session.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      )}
      <TranscriptViewer
        transcript={selectedSession.transcript}
        ariaLabel={`${selectedSession.label} conversation`}
        isScrollable={isScrollable}
        fillHeight={fillHeight}
        heading={heading}
        headerAction={headerAction}
        renderTurnAction={renderTurnAction}
        renderTurnAttachment={renderTurnAttachment}
      />
    </Fragment>
  );
}
